import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import "./EditPlans.css";
import { FaTrashAlt } from "react-icons/fa";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useTranslation } from "react-i18next";

function EditPlans() {
  const {t}=useTranslation('backend');
  const BASE_URL = process.env.REACT_APP_BASE_URL;
  const navigate = useNavigate();
  const storedPlanName = localStorage.getItem("plan_name");

  const [editablePlanName, setEditablePlanName] = useState(storedPlanName);
  const [editablePlanNameAr, setEditablePlanNameAr] = useState("");  // Arabic name
  const [amount, setAmount] = useState("");
  const [duration, setDuration] = useState("");
  const [status, setStatus] = useState("active");
  const [features, setFeatures] = useState([]);
  const [priority, setPriority] = useState(0);
  const [originalData, setOriginalData] = useState({});

  useEffect(() => {
    fetchPackagedetails();
  }, []);

const fetchPackagedetails = async () => {
  try {
    const response = await fetch(`${BASE_URL}/api/plans/edit/${storedPlanName}`, {
      method: "GET",
      headers: { "Content-Type": "application/json" },
    });

    if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

    const result = await response.json();
    const planDetails = result.result;

    // Parse features if it's a string
    const rawFeatures =
      typeof planDetails.features === "string"
        ? JSON.parse(planDetails.features)
        : planDetails.features ?? [];

    setOriginalData({
      ...planDetails,
      features: rawFeatures, // store parsed version for change detection
    });

    setAmount(planDetails.amount);
    setDuration(planDetails.duration);
    setStatus(planDetails.status);
    setPriority(planDetails.priority);
    setEditablePlanName(planDetails.plan_name);
    setEditablePlanNameAr(planDetails.plan_name_ar || "");

    const updatedFeatures = rawFeatures.map((feature) => {
      if (typeof feature === "string") return { text: feature, text_ar: "" };
      return { text: feature.text || "", text_ar: feature.text_ar || "" };
    });

    setFeatures(updatedFeatures);
  } catch (err) {
    console.error("Fetch error:", err);
  }
};

  const handleFeatureChange = (index, key, value) => {
    const updatedFeatures = [...features];
    updatedFeatures[index][key] = value;
    setFeatures(updatedFeatures);
  };

  const handleAddFeature = () => {
    setFeatures([...features, { text: "", text_ar: "" }]);
  };

  const handleDeleteFeature = (index) => {
    const isConfirmed = window.confirm(t("confirmFeatureDelete"));
    if (isConfirmed) {
      const updatedFeatures = features.filter((_, i) => i !== index);
      setFeatures(updatedFeatures);
    }
  };

  const hasDataChanged = () => {
    return (
      amount !== originalData.amount ||
      duration !== originalData.duration ||
      status !== originalData.status ||
      priority !== originalData.priority ||
      editablePlanName !== originalData.plan_name ||
      editablePlanNameAr !== originalData.plan_name_ar ||   // Check Arabic name
      JSON.stringify(features) !== JSON.stringify(originalData.features)
    );
  };

  const handleSaveChanges = async (e) => {
    e.preventDefault();

    if (!hasDataChanged()) {
      toast.error("No data has been edited.");
      return;
    }

    const updatedData = {
      new_plan_name: editablePlanName,         // English name
      new_plan_name_ar: editablePlanNameAr,    // Arabic name
      amount,
      duration,
      status,
      features,
      priority,
    };

    try {
      const response = await fetch(`${BASE_URL}/api/plans/update/${storedPlanName}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(updatedData),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      // Update local storage with the new plan name if changed
      if (editablePlanName !== storedPlanName) {
        localStorage.setItem("plan_name", editablePlanName);
      }

      toast.success("Plan updated successfully!");
      navigate("/dashboard/categories");
    } catch (err) {
      console.error("Save error:", err);
      toast.error("There was an error saving the changes.");
    }
  };

  const handleBack = (e) => {
    e.preventDefault();
    navigate("/dashboard/categories");
  };

  return (
    <div className="edit-plan-container">
      <h2>{t('edit_plan')}</h2>

      <form className="edit-plan-form" onSubmit={handleSaveChanges}>
        
        {/* Plan Name (English) */}
        <div className="form-group">
          <label dir="ltr">Plan Name (English):</label>
          <div className="value-field">
            <input
              type="text"
              value={editablePlanName}
              onChange={(e) => setEditablePlanName(e.target.value)}
              dir="ltr"
            />
          </div>
        </div>

        {/* Plan Name (Arabic) */}
        <div className="form-group">
          <label>اسم الخطة (بالعربية):</label>
          <div className="value-field">
            <input
              type="text"
              value={editablePlanNameAr}
              onChange={(e) => setEditablePlanNameAr(e.target.value)}
            />
          </div>
        </div>

        {/* Amount */}
        <div className="form-group">
          <label>{t('amount_sar')}</label>
          <div className="value-field">
            <input
              type="number"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
            />
          </div>
        </div>

        {/* Duration */}
        <div className="form-group">
          <label>{t('duration')}</label>
          <div className="value-field">
            <input
              type="number"
              value={duration}
              onChange={(e) => setDuration(e.target.value)}
            />
          </div>
        </div>

        {/* Status */}
        <div className="form-group">
          <label>{t('status')}:</label>
          <div className="value-field">
            <select
              name="status"
              value={status}
              onChange={(e) => setStatus(e.target.value)}
            >
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>
        </div>

        {/* Priority */}
        <div className="form-group">
          <label>{t('priority')}:</label>
          <div className="value-field">
            <input
              type="number"
              value={priority}
              onChange={(e) => setPriority(e.target.value)}
            />
          </div>
        </div>

        {/* Features */}
        <div className="form-group">
          <div style={{ display: "flex", fontSize: "28px" }}>
            <label className="features">{t('features')}:</label>
            <label className="add-features" onClick={handleAddFeature}>
              +
            </label>
          </div>
          <div className="features-containers">
            {features.map((feature, index) => (
              <div key={index} className="feature-items">
                <div className="feature-field" style={{ color: "black" }}>
                  <label>{index + 1}</label>

                  <input
                    type="text"
                    placeholder="Feature (English)"
                    value={feature.text}
                    onChange={(e) =>
                      handleFeatureChange(index, "text", e.target.value)
                    }
                  />

                  <input
                    type="text"
                    placeholder="الميزة (بالعربية):"
                    value={feature.text_ar}
                    onChange={(e) =>
                      handleFeatureChange(index, "text_ar", e.target.value)
                    }
                  />

                  <button
                    type="button"
                    onClick={() => handleDeleteFeature(index)}
                    className="delete-btn dlt"
                  >
                    <FaTrashAlt />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Buttons */}
        <div className="save-back-btn-class">
          <button className="back-btn" onClick={handleBack}>{t('back')}</button>
          <button type="submit" className="submit-button">{t('save')}</button>
        </div>
      </form>
      <ToastContainer position="bottom-center" />
    </div>
  );
}

export default EditPlans;
